import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PersonIcon from '@material-ui/icons/Person';
import GroupIcon from '@material-ui/icons/Group';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { spassuCodeApiRef } from '../../api';
import type { SpassuCodeApi } from '../../api';
import type {
  AzureRepository,
  ProviderId,
  Repository,
  Role,
} from '../../api/types';
import { ROLE_LABELS } from '../../api/types';
import { PROJECT_SCOPE_LABELS } from '../../utils/scopeLabels';
import type { ScopeLabels } from '../../utils/scopeLabels';

/**
 * Lista os repositórios de um `project_key` pelos endpoints `/scm/*`. Em
 * providers com camada de projeto (Azure DevOps) o `projectKey` é o projeto
 * sob `owner`; nos flat (GitHub) é o próprio owner. Adapta ao shape legado
 * `AzureRepository` consumido pela lista — a membership guarda só o `name`
 * no campo `repositoryKey`.
 */
async function loadRepositoriesForProject(
  api: SpassuCodeApi,
  providerId: ProviderId,
  owner: string | undefined,
  projectKey: string,
): Promise<AzureRepository[]> {
  const providers = await api.listScmProviders();
  const descriptor = providers.find(p => p.id === providerId);
  if (!descriptor) {
    throw new Error(`Provider '${providerId}' is not registered`);
  }
  const effectiveOwner = owner ?? projectKey;
  const repos: Repository[] = descriptor.hierarchy.hasProject
    ? await api.listScmRepositories(providerId, effectiveOwner, projectKey)
    : await api.listScmRepositories(providerId, effectiveOwner);
  return repos.map<AzureRepository>(r => ({
    id: r.externalId,
    name: r.name,
    defaultBranch: r.defaultBranch ?? undefined,
    webUrl: r.webUrl,
    remoteUrl: r.cloneUrl,
  }));
}

interface EntityOption {
  entityRef: string;
  displayName: string;
  kind: 'User' | 'Group';
}

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 400,
    marginTop: theme.spacing(1),
  },
  error: {
    color: theme.palette.error.main,
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  optionIcon: {
    color: theme.palette.text.secondary,
    fontSize: 20,
  },
  optionText: {
    display: 'flex',
    flexDirection: 'column',
  },
  optionRef: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  repoSection: {
    marginTop: theme.spacing(1),
  },
  repoList: {
    maxHeight: 200,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: 0,
  },
  repoItem: {
    paddingTop: 2,
    paddingBottom: 2,
  },
}));

export interface AddMemberDialogProps {
  open: boolean;
  projectKey: string;
  /**
   * Provider de origem do projeto. Quando informado, a lista de repositórios
   * vem do endpoint multi-provider `/scm/<provider>/owners/<owner>/...`.
   * Sem provider (legado), cai no `listRepositories(projectKey)` que percorre
   * `/azure/*`.
   */
  providerId?: ProviderId;
  /**
   * Owner/organização dona do projeto. Em providers com camada de projeto
   * (Azure DevOps) o `projectKey` é o projeto e os repositórios são listados
   * sob `(owner, projectKey)`; em providers flat (GitHub) `owner === projectKey`.
   */
  owner?: string;
  /** Rótulos da entidade selecionada ("Projeto" ou "Organização"). */
  scopeLabels?: ScopeLabels;
  onClose: () => void;
  onAdded: () => void;
}

export const AddMemberDialog = ({
  open,
  projectKey,
  providerId,
  owner,
  scopeLabels = PROJECT_SCOPE_LABELS,
  onClose,
  onAdded,
}: AddMemberDialogProps) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [selected, setSelected] = useState<EntityOption | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<EntityOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [role, setRole] = useState<Role>('desenvolvedor');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Expiration
  const [hasExpiration, setHasExpiration] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');

  // Assignable roles (fetched from backend based on caller's permissions)
  const [assignableRoles, setAssignableRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Repository selection
  const [repositories, setRepositories] = useState<AzureRepository[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [reposError, setReposError] = useState<string | null>(null);
  const [allRepos, setAllRepos] = useState(true);
  const [selectedRepos, setSelectedRepos] = useState<Set<string>>(new Set());

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelected(null);
      setInputValue('');
      setOptions([]);
      setRole('desenvolvedor');
      setError(null);
      setReposError(null);
      setHasExpiration(false);
      setExpiresAt('');
      setAllRepos(true);
      setSelectedRepos(new Set());
    }
  }, [open]);

  // Load assignable roles when dialog opens
  useEffect(() => {
    if (!open || !projectKey) {
      return undefined;
    }
    let cancelled = false;
    setLoadingRoles(true);
    api
      .getAssignableRoles(projectKey)
      .then(roles => {
        if (!cancelled) {
          setAssignableRoles(roles);
          // Reset selected role to first assignable if current is not allowed
          if (roles.length > 0 && !roles.includes(role)) {
            setRole(roles[0]);
          }
          setLoadingRoles(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadingRoles(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectKey, api, role]);

  // Carrega os repositórios do project_key selecionado, na mesma chave que os
  // gates do backend usam: em providers com camada de projeto (Azure DevOps)
  // `projectKey` é o projeto e os repos vêm de `(owner, projectKey)`; em
  // providers flat (GitHub) `projectKey === owner`. Quando providerId é
  // undefined (legado), cai no listRepositories que percorre `/azure/*`.
  useEffect(() => {
    if (!open || !projectKey) {
      return undefined;
    }
    let cancelled = false;
    setLoadingRepos(true);
    setReposError(null);

    const loader: Promise<AzureRepository[]> =
      providerId !== undefined
        ? loadRepositoriesForProject(api, providerId, owner, projectKey)
        : api.listRepositories(projectKey);

    loader
      .then(repos => {
        if (!cancelled) {
          setRepositories(repos);
          setLoadingRepos(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          // Sinalizar a falha explicitamente — antes a lista vinha vazia
          // sem distinção de "owner sem repos" vs "carregamento falhou", o
          // que confundia o usuário e ele não sabia se precisava reabrir
          // o dialog ou abrir um chamado.
          setReposError(
            `Falha ao carregar repositórios: ${
              err instanceof Error ? err.message : String(err)
            }`,
          );
          setLoadingRepos(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectKey, providerId, owner, api]);

  // Search Catalog
  const searchEntities = useCallback(
    async (query: string) => {
      if (query.length < 2) {
        setOptions([]);
        return;
      }
      setSearching(true);
      try {
        const response = await catalogApi.getEntities({
          filter: [{ kind: 'User' }, { kind: 'Group' }],
        });
        const filtered: EntityOption[] = (response.items ?? [])
          .filter(entity => {
            const name = (entity.metadata?.name ?? '').toLowerCase();
            const profile = (entity.spec as Record<string, unknown>)
              ?.profile as Record<string, unknown> | undefined;
            const title = (profile?.displayName as string) ?? '';
            const q = query.toLowerCase();
            return name.includes(q) || title.toLowerCase().includes(q);
          })
          .slice(0, 30)
          .map(entity => {
            const kind = entity.kind as 'User' | 'Group';
            const namespace = entity.metadata?.namespace ?? 'default';
            const name = entity.metadata?.name ?? '';
            const profile = (entity.spec as Record<string, unknown>)
              ?.profile as Record<string, unknown> | undefined;
            const displayName = (profile?.displayName as string) ?? name;
            return {
              entityRef: `${kind.toLowerCase()}:${namespace}/${name}`,
              displayName,
              kind,
            };
          });
        setOptions(filtered);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    },
    [catalogApi],
  );

  // Debounced search
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setOptions([]);
      return undefined;
    }
    const timer = setTimeout(() => searchEntities(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue, searchEntities]);

  const handleToggleRepo = (repoName: string) => {
    setSelectedRepos(prev => {
      const next = new Set(prev);
      if (next.has(repoName)) next.delete(repoName);
      else next.add(repoName);
      return next;
    });
  };

  const handleToggleAll = () => {
    setAllRepos(prev => !prev);
    if (!allRepos) {
      setSelectedRepos(new Set());
    }
  };

  const minExpirationDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  })();

  const autocompleteNoOptionsText =
    inputValue.length < 2
      ? 'Digite ao menos 2 caracteres'
      : 'Nenhum resultado. Você pode digitar o entity ref manualmente.';

  const handleSubmit = async () => {
    const entityRef = selected?.entityRef ?? inputValue.trim();
    if (!entityRef) {
      setError('Selecione um usuário ou grupo');
      return;
    }
    if (!selected && !/^(user|group):[^/]+\/.+$/.test(entityRef)) {
      setError(
        'Formato inválido. Use: user:default/nome ou group:default/time',
      );
      return;
    }
    if (!allRepos && selectedRepos.size === 0) {
      setError(
        'Selecione ao menos um repositório ou marque "Todos os repositórios"',
      );
      return;
    }
    if (hasExpiration && !expiresAt) {
      setError('Selecione a data de expiração');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const repoKeys = allRepos ? undefined : Array.from(selectedRepos);
      let expiration: string | undefined;
      if (hasExpiration && expiresAt) {
        const d = new Date(`${expiresAt}T23:59:59.999Z`);
        if (isNaN(d.getTime())) {
          setError('Data de expiração inválida');
          return;
        }
        expiration = d.toISOString();
      }
      await api.addProjectMember(
        projectKey,
        entityRef,
        role,
        expiration,
        repoKeys,
      );
      onAdded();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Adicionar Membro {scopeLabels.toThe}</DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <Autocomplete
            freeSolo
            options={options}
            noOptionsText={autocompleteNoOptionsText}
            getOptionLabel={opt =>
              typeof opt === 'string' ? opt : opt.displayName
            }
            renderOption={opt => (
              <div className={classes.optionRow}>
                {opt.kind === 'Group' ? (
                  <GroupIcon className={classes.optionIcon} />
                ) : (
                  <PersonIcon className={classes.optionIcon} />
                )}
                <div className={classes.optionText}>
                  <span>{opt.displayName}</span>
                  <span className={classes.optionRef}>{opt.entityRef}</span>
                </div>
              </div>
            )}
            value={selected}
            onChange={(_e, newValue) => {
              if (typeof newValue === 'string') {
                setSelected(null);
                setInputValue(newValue);
              } else {
                setSelected(newValue);
              }
            }}
            inputValue={inputValue}
            onInputChange={(_e, newInput) => setInputValue(newInput)}
            loading={searching}
            renderInput={params => (
              <TextField
                {...params}
                label="Usuário ou Grupo"
                placeholder="Buscar por nome..."
                helperText="Busca usuários e grupos do Backstage Catalog"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControl
            fullWidth
            disabled={loadingRoles || assignableRoles.length === 0}
          >
            <InputLabel>Role</InputLabel>
            <Select
              value={role}
              onChange={e => setRole(e.target.value as Role)}
            >
              {assignableRoles.map(r => (
                <MenuItem key={r} value={r}>
                  {ROLE_LABELS[r]}
                </MenuItem>
              ))}
            </Select>
            {!loadingRoles && assignableRoles.length === 0 && (
              <Typography variant="caption" color="error">
                Você não tem permissão para atribuir roles {scopeLabels.inThis}
              </Typography>
            )}
          </FormControl>

          {/* Expiration */}
          <FormControlLabel
            control={
              <Checkbox
                checked={hasExpiration}
                onChange={() => setHasExpiration(prev => !prev)}
                color="primary"
              />
            }
            label="Acesso temporário (com data de expiração)"
          />
          {hasExpiration && (
            <TextField
              label="Expira em"
              type="date"
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minExpirationDate }}
              fullWidth
              helperText="O acesso será removido automaticamente após esta data"
            />
          )}

          {/* Repository selection */}
          <div className={classes.repoSection}>
            <Typography variant="subtitle2" gutterBottom>
              Repositórios
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allRepos}
                  onChange={handleToggleAll}
                  color="primary"
                />
              }
              label="Todos os repositórios"
            />
            {!allRepos && (
              <>
                <Divider />
                {loadingRepos && (
                  <Typography variant="body2" color="textSecondary">
                    Carregando repositórios...
                  </Typography>
                )}
                {!loadingRepos && reposError && (
                  <Typography variant="body2" className={classes.error}>
                    {reposError}
                  </Typography>
                )}
                {!loadingRepos && !reposError && (
                  <List dense className={classes.repoList}>
                    {repositories.map(repo => (
                      <ListItem
                        key={repo.name}
                        button
                        dense
                        className={classes.repoItem}
                        onClick={() => handleToggleRepo(repo.name)}
                      >
                        <ListItemIcon style={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedRepos.has(repo.name)}
                            tabIndex={-1}
                            disableRipple
                            color="primary"
                            size="small"
                          />
                        </ListItemIcon>
                        <ListItemText primary={repo.name} />
                      </ListItem>
                    ))}
                    {repositories.length === 0 && (
                      <ListItem>
                        <ListItemText
                          primary="Nenhum repositório encontrado"
                          primaryTypographyProps={{
                            color: 'textSecondary',
                            variant: 'body2',
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                )}
              </>
            )}
          </div>

          {error && (
            <Typography variant="body2" className={classes.error}>
              {error}
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={
            submitting ||
            (!selected && !inputValue.trim()) ||
            assignableRoles.length === 0
          }
        >
          {submitting ? 'Adicionando...' : 'Adicionar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
